import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { Vacancy } from '../models/vacancy.model';

const TITLES = [
  'Technisch manager',
  'Software Developer',
  'Project Manager',
  'Sales Manager',
  'Data Analyst',
  'UX Designer',
  'HR Specialist',
  'Marketing Manager',
];
const COMPANIES = ['Agro', 'TechCorp', 'InnovateBV', 'BuildCo', 'MediaGroup', 'FinanceNL', 'RetailPro', 'HealthCare+'];
const LOCATIONS = ['Utrecht', 'Amsterdam', 'Rotterdam', 'Den Haag', 'Eindhoven', 'Groningen', 'Maastricht', 'Arnhem'];
const TAGS = ['40+', 'HBO', 'WO', 'MBO', 'Fulltime', 'Parttime', '32u', '36u'];
const KNOWLEDGE_SKILLS = [
  'Data analysis',
  'Project management',
  'Microsoft Excel',
  'Python',
  'JavaScript',
  'SQL',
  'Marketing strategy',
  'Financial planning',
];
const SOFT_SKILLS = [
  'Negotiation',
  'Critical thinking',
  'Leadership',
  'Communication',
  'Problem solving',
  'Teamwork',
  'Creativity',
  'Adaptability',
  'Time management',
];
const LANGUAGES = ['Nederlands', 'Engels', 'Duits', 'Frans', 'Spaans'];

const STORAGE_KEY = 'savedVacatures';

function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function generateVacancies(count: number): any[] {
  return Array.from({ length: count }, (_, i) => {
    const knowledge = pickRandom(KNOWLEDGE_SKILLS, 2 + Math.floor(Math.random() * 4));
    const softSkills = pickRandom(SOFT_SKILLS, 3 + Math.floor(Math.random() * 4));
    const languages = pickRandom(LANGUAGES, 1 + Math.floor(Math.random() * 3));

    return {
      id: i + 1,
      title: TITLES[i % TITLES.length],
      company: COMPANIES[i % COMPANIES.length],
      location: LOCATIONS[i % LOCATIONS.length],
      tags: [TAGS[i % TAGS.length], TAGS[(i + 1) % TAGS.length]],
      skills: [...knowledge, ...softSkills, ...languages],
      skillCategories: { knowledge, softSkills, languages },
      matchScore: Math.floor(60 + Math.random() * 40),
      isNew: i < 10,
      hours: ['32-36 uur per week', '36-40 uur per week', '24-32 uur per week'][i % 3],
      educationLevel: ['HBO', 'WO', 'MBO'][i % 3],
      salary: ['€3,500 - €5,000', '€4,583 - €6,250', '€5,667 - €10,000'][i % 3],
      postedDate: ['2 weken geleden', '1 week geleden', '3 dagen geleden', 'Vandaag'][i % 4],
      description: `**${TITLES[i % TITLES.length]} - ${LOCATIONS[i % LOCATIONS.length]}**

Als ${TITLES[
        i % TITLES.length
      ].toLowerCase()} heb je een belangrijke rol binnen de organisatie. Jij en jouw team zijn verantwoordelijk voor het realiseren van de doelstellingen. Je bent het aanspreekpunt voor collega's en de spin in het web als het gaat om operationele zaken.

Je draagt de verantwoordelijkheid voor verschillende resultaten en werkt samen met het team aan het realiseren van doelstellingen op het gebied van kwaliteit, efficiëntie en medewerkerstevredenheid. Daarnaast ben jij verantwoordelijk voor de kwaliteit van de dienstverlening en de veiligheid.

Je denkt actief mee over het succes van jouw afdeling en vindt altijd een creatieve oplossing als er iets niet volgens plan verloopt. Je stuurt het team aan en zorgt ervoor dat alles vlekkeloos verloopt.`,
      skillsMatch: knowledge.slice(0, 3).map((name) => ({ name, level: 'match' as const })),
      skillsClose: softSkills.slice(0, 4).map((name) => ({ name, level: 'close' as const })),
      skillsGap: knowledge.slice(3, 5).map((name) => ({ name, level: 'gap' as const })),
    };
  }).sort((a, b) => b.matchScore - a.matchScore);
}

@Injectable({ providedIn: 'root' })
export class VacancyMockService {
  private vacancies = generateVacancies(100);

  getVacancies(): Observable<Vacancy[]> {
    return of(this.vacancies);
  }

  getVacancyById(id: number): Observable<Vacancy | undefined> {
    return of(this.vacancies.find((v) => v.id === id));
  }

  getSavedVacancies(): Observable<Vacancy[]> {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return of([]);
    const vacancies: Vacancy[] = JSON.parse(saved);
    return of(vacancies.sort((a, b) => b.matchScore - a.matchScore));
  }

  getSavedIds(): number[] {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    return (JSON.parse(saved) as Vacancy[]).map((v) => v.id);
  }

  toggleSaveVacancy(vacancy: Vacancy): boolean {
    const saved = localStorage.getItem(STORAGE_KEY);
    const list: Vacancy[] = saved ? JSON.parse(saved) : [];
    const exists = list.some((v) => v.id === vacancy.id);

    if (exists) {
      const updated = list.filter((v) => v.id !== vacancy.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return false;
    } else {
      list.push(vacancy);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      return true;
    }
  }
}
